import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import GoogleSignInButton from './GoogleSignInButton';
import { BusyContent } from './ui/Primitives';
import { googleAuthRequest } from '../services/googleAuthApi';
import { getBackendHost } from '../utils/backendHost';

export default function AccountDeletion({ methods }) {
  const [options, setOptions] = useState(null);
  const [open, setOpen] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const dialog = useRef(null);
  const trigger = useRef(null);
  useEffect(() => {
    let active = true;
    googleAuthRequest('account-deletion', { method: 'GET', authenticated: true })
      .then(value => { if (active) setOptions(value); })
      .catch(() => { if (active) setError('Deletion options could not load. Refresh the page to try again.'); });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    if (open) dialog.current.showModal();
    return () => { if (open) trigger.current?.focus(); };
  }, [open]);
  const finish = result => {
    localStorage.removeItem('jwt_authorization');
    localStorage.removeItem('onboarding-ai');
    sessionStorage.removeItem('sayable_observability_queue');
    sessionStorage.setItem('accountDeletion', JSON.stringify(result));
    // Reload the application to discard authenticated state and cached organization data.
    window.location.replace('/deletion-status');
  };
  const submit = async event => {
    event.preventDefault();
    setBusy(true); setError('');
    try {
      finish(await googleAuthRequest('account-deletion', { authenticated: true,
        body: { scope: options.scope, confirmation, password } }));
    } catch (failure) { setError(failure.message); setBusy(false); }
  };
  const valid = confirmation === options?.confirmation;
  const organization = options?.scope === 'organization';
  return <section className="account-security-card account-deletion-card">
    <h2>Delete account</h2>
    <p>{organization ? 'Permanently delete your organization and every team member’s account.' : 'Permanently delete your personal Sayable account.'}</p>
    {!open && error && <p role="alert" className="account-error">{error}</p>}
    <button ref={trigger} type="button" className="account-delete-button" disabled={!options || !methods}
      onClick={() => { setConfirmation(''); setPassword(''); setError(''); setOpen(true); }}>Delete account</button>
    {open && <dialog ref={dialog} className="account-delete-dialog" aria-labelledby="delete-account-title"
      onCancel={event => { if (busy) event.preventDefault(); else setOpen(false); }}>
      <h2 id="delete-account-title">{organization ? `Delete ${options.name}?` : 'Delete your account?'}</h2>
      {organization ? <>
        <p>This permanently deletes your organization, all {options.memberCount} member accounts, calls, recordings, messages, knowledge, and settings. Everyone loses access immediately.</p>
        <p>Your subscription will be canceled, your Sayable phone numbers released, and connected services disconnected. Released phone numbers may not be recoverable.</p>
        <p>Records already sent to your own calendar or CRM remain there. Providers may retain billing and historical records under their policies; backup copies expire under their retention schedules.</p>
      </> : <p>Your login, personal settings, reminders, and notes will be deleted. The organization’s shared calls, messages, billing, phone numbers, and service connections remain available to the team.</p>}
      <p><strong>This cannot be undone.</strong> Type DELETE ACCOUNT and verify your identity to continue.</p>
      <form onSubmit={submit} className="account-method-form">
        <label htmlFor="delete-account-confirmation">Type DELETE ACCOUNT</label>
        <input id="delete-account-confirmation" autoComplete="off" value={confirmation} onChange={event => setConfirmation(event.target.value)} disabled={busy} />
        {methods.hasPassword && <>
          <label htmlFor="delete-account-password">Current password</label>
          <input id="delete-account-password" type="password" autoComplete="current-password" value={password} onChange={event => setPassword(event.target.value)} disabled={busy} />
          <button type="submit" className="account-delete-button" disabled={!valid || !password || busy}>
            <BusyContent busy={busy} label="Requesting deletion…">Permanently delete account</BusyContent>
          </button>
        </>}
      </form>
      {methods.google.connected && methods.googleAvailable && <>
        <p>Confirming with your connected Google account also authorizes this deletion.</p>
        <GoogleSignInButton purpose="delete" disabled={!valid || busy} payload={{ scope: options.scope, confirmation }} onSuccess={finish} />
      </>}
      {error && <p role="alert" className="account-error">{error}</p>}
      <button type="button" className="account-cancel" disabled={busy} onClick={() => setOpen(false)}>Keep my account</button>
    </dialog>}
  </section>;
}

export function AccountDeletionStatus() {
  const [result, setResult] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('accountDeletion')) || null; } catch { return null; }
  });
  const [error, setError] = useState('');
  useEffect(() => {
    if (!result?.receipt || result.status === 'completed') return;
    let active = true;
    let timer;
    const refresh = async () => {
      try {
        const response = await fetch(`${getBackendHost()}/auth/account-deletion/status`, {
          headers: { Authorization: `Bearer ${result.receipt}` }, cache: 'no-store',
        });
        if (!response.ok) throw new Error('Status is temporarily unavailable. Your deletion request remains recorded.');
        const update = await response.json();
        if (!active) return;
        setError('');
        if (update.status === 'completed') {
          const completed = { status: 'completed', scope: 'organization' };
          sessionStorage.setItem('accountDeletion', JSON.stringify(completed));
          setResult(completed);
          return;
        }
        setResult(previous => ({ ...previous, ...update }));
      } catch (failure) { if (active) setError(failure.message); }
      if (active) timer = setTimeout(refresh, 10000);
    };
    refresh();
    return () => { active = false; clearTimeout(timer); };
  }, [result?.receipt, result?.status === 'completed']);
  return <div className="page-content account-page"><section className="account-security-card">
    <h1>{result?.status === 'completed' ? 'Account deleted' : result ? 'Account deletion requested' : 'Account deletion status'}</h1>
    <p role="status">{result?.status === 'completed'
      ? result.scope === 'member' ? 'Your personal account has been deleted. Your organization’s shared records remain with the team.' : 'Your Sayable account and organization data have been deleted, and service cleanup is complete. Provider and backup retention policies still apply.'
      : result?.status === 'needs_attention' ? 'Account access remains disabled. Deletion needs assistance to finish.'
      : result ? 'Everyone covered by this request has been signed out. We are removing data and closing service connections. Any unfinished steps will be retried automatically.'
      : 'To delete an account, sign in and open Account settings.'}</p>
    {result?.status === 'needs_attention' && <p role="alert">Deletion needs assistance. Please contact support with the reference below; your account remains disabled.</p>}
    {error && <p role="alert">{error}</p>}
    {result?.reference && <p>Support reference: {result.reference}</p>}
    <p>For help, contact <a href="mailto:support@sayable.ai">support@sayable.ai</a>.</p>
    <Link to="/">Return to Sayable</Link>
  </section></div>;
}
